import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ShoppingBag, Menu, X, Sparkles } from "lucide-react";
import { useCart } from "../context/CartContext";

export function LogoMark({ className = "w-8 h-8" }) {
  return (
    <svg viewBox="0 0 64 64" className={className} aria-hidden="true">
      <rect width="64" height="64" rx="10" fill="#F2F2EF" />
      <text x="29" y="44" fontFamily="Syne, Arial, sans-serif" fontSize="33" fontWeight="800" fill="#0A0A0B" textAnchor="middle" letterSpacing="-2">01</text>
      <rect x="46" y="16" width="6" height="6" fill="#E11D48" />
    </svg>
  );
}

const links = [
  { to: "/shop", label: "SHOP" },
  { to: "/#story", label: "STORY", hash: "story" },
  { to: "/#reviews", label: "REVIEWS", hash: "reviews" },
  { to: "/#faq", label: "FAQ", hash: "faq" },
];

export default function Nav() {
  const { count, openCart } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setMenuOpen(false), [location]);

  const openNewsletter = () => window.dispatchEvent(new CustomEvent("open-newsletter"));

  return (
    <header className="fixed top-0 inset-x-0 z-50">
      <div className="bg-bone text-ink border-b border-ink/10">
        <div className="max-w-[1500px] mx-auto px-5 md:px-10 h-8 flex items-center justify-center gap-6 font-mono text-[10px] md:text-[11px] tracking-[0.18em] uppercase">
          <span>Free UK delivery over £75</span>
          <span className="hidden md:inline-block w-1.5 h-1.5 rotate-45 bg-ink/70" />
          <span className="hidden md:inline">14-day returns</span>
          <span className="hidden md:inline-block w-1.5 h-1.5 rotate-45 bg-ink/70" />
          <span className="hidden lg:inline">Exclusively made in London</span>
        </div>
      </div>

      <div
        className={`border-b transition-colors duration-500 ${
          scrolled ? "bg-ink/85 backdrop-blur-xl border-white/10" : "bg-ink/40 backdrop-blur-md border-transparent"
        }`}
      >
        <div className="max-w-[1500px] mx-auto px-5 md:px-10 h-16 md:h-[72px] flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group" data-testid="header-logo">
            <LogoMark className="w-8 h-8 transition-transform duration-500 group-hover:rotate-[-6deg]" />
            <span className="font-display font-800 font-extrabold tracking-tight text-lg md:text-xl">01 LONDON</span>
          </Link>

          <nav className="hidden md:flex items-center gap-8 font-mono text-[11px] tracking-[0.22em]">
            {links.map((l) => (
              <NavLink
                key={l.label}
                to={l.to}
                className="text-ash hover:text-bone transition-colors duration-300 relative group py-2"
                data-testid={`nav-${l.label.toLowerCase()}-link`}
              >
                {l.label}
                <span className="absolute left-0 bottom-0 h-px w-0 bg-bone transition-all duration-400 group-hover:w-full" />
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3 md:gap-5">
            <button
              onClick={openNewsletter}
              className="hidden sm:flex items-center gap-2 font-mono text-[10px] tracking-[0.18em] border border-ember/60 text-bone bg-ember/10 hover:bg-ember hover:text-white px-4 py-2 transition-colors duration-300"
              data-testid="nav-offer-button"
            >
              <Sparkles className="w-3.5 h-3.5" />
              10% OFF
            </button>
            <button
              onClick={openCart}
              className="relative p-2 hover:opacity-70 transition-opacity duration-300"
              aria-label="Open cart"
              data-testid="cart-trigger-button"
            >
              <ShoppingBag className="w-5 h-5" strokeWidth={1.5} />
              <AnimatePresence>
                {count > 0 && (
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    exit={{ scale: 0 }}
                    className="absolute -top-0.5 -right-0.5 w-4.5 h-4.5 min-w-[18px] min-h-[18px] bg-ember text-white text-[9px] font-bold flex items-center justify-center rounded-full px-1"
                    data-testid="cart-count-badge"
                  >
                    {count}
                  </motion.span>
                )}
              </AnimatePresence>
            </button>
            <button className="md:hidden p-2" onClick={() => setMenuOpen(true)} aria-label="Open menu" data-testid="mobile-menu-open-button">
              <Menu className="w-5 h-5" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-ink flex flex-col"
          >
            <div className="h-16 px-5 flex items-center justify-between border-b border-white/10">
              <span className="font-display font-extrabold tracking-tight">01 LONDON</span>
              <button className="p-2" onClick={() => setMenuOpen(false)} aria-label="Close menu" data-testid="mobile-menu-close-button">
                <X className="w-6 h-6" strokeWidth={1.5} />
              </button>
            </div>
            <nav className="flex flex-col justify-center flex-1 px-8 gap-2">
              {links.map((l, i) => (
                <motion.div
                  key={l.label}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.07 }}
                >
                  <Link to={l.to} className="font-display font-extrabold uppercase text-5xl tracking-tight py-3 block hover:text-ash transition-colors" data-testid={`mobile-nav-${l.label.toLowerCase()}`}>
                    {l.label}
                  </Link>
                </motion.div>
              ))}
              <button onClick={openNewsletter} className="mt-8 text-left font-mono text-xs tracking-[0.2em] text-ember uppercase" data-testid="mobile-offer-button">
                Get 10% off your first order →
              </button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
